import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { CardsThree } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { useParams } from 'react-router-dom';

import { sendBulkFaceEmail } from '../../../../UtilsEmail';

import { Button, Alert } from '../../../../components/general';
import { PageSubHeader } from '../../../../components/layout/content';
import { getWildcardTextMaximum } from '../../../../components/video';

import { URL, FACE_EMAIL_STATUS } from '../../../../constants';

import { useProjectStore } from '../../../../stores/project';
import { useVideoBuilderStore } from '../../../../stores/videoBuilder';

import {
  Person,
  MAClips,
  PeopleEmailState,
  HlReel,
  Shot,
  SGTemplate,
} from '../../../../types';
import { isEmpty, getPluralPhrase, round, getUrl } from '../../../../utils';

import PersonClipsModal from '../../../VideoBuilder/Analysis/components/personClipsModal';
import {
  getClipSelections,
  getPersonWildcardsData,
} from '../../../VideoBuilder/components/getAnalysisData';
import EmailPreviewModal from '../../Settings/EmailHubTab/components/emailPreviewModal';

import MaAccordion from './maAccordion';
import MaPersonBlock from './maPersonBlock';

interface Props {
  isAnalysed: boolean;
  people: Person[];
  hlReel: HlReel | null;
  requiredShots: Shot[];
  emailTemplate: SGTemplate;
}

const MaClipsBlock: React.FC<Props> = (props) => {
  const { eventId = '' } = useParams();

  const { isAnalysed, people, hlReel, requiredShots, emailTemplate } = props;

  const { currentProject } = useProjectStore();
  const { setClipSelections } = useVideoBuilderStore();

  const [emailState, setEmailState] = useState<PeopleEmailState>({});
  const [isEmailsSending, setIsEmailsSending] = useState(false);

  const [openedPersonId, setOpenedPersonId] = useState<string | null>(null);

  const [showEmailPreviewModal, setShowEmailPreviewModal] = useState(false);

  const getClipsDataObj = useCallback(
    (array: Shot[], id?: string) => ({
      share_url:
        `${getUrl(URL.CHECK_IN, eventId, id, true)}` +
        `?reqAssets=clips-${array.map((clip) => round(clip.duration)).join(',')}`,
      text:
        getWildcardTextMaximum(array, 'video', 'around') +
        ` in <b>${currentProject?.videoMode}</b> orientation`,
    }),
    [eventId, currentProject],
  );

  // clip selections – find the matching clips for each person's wildcard spot
  // clipSelectionsDefault – stays same and defines completePeople and incompletePeople
  // clipSelections – state that can be changed when re-selecting in personClipsModal

  const clipSelectionsDefault = useMemo(
    () => getClipSelections({ people, requiredShots, isIncludeDuration: true }),
    [people, requiredShots],
  );

  useEffect(() => {
    setClipSelections(clipSelectionsDefault);
  }, [clipSelectionsDefault]);

  useEffect(() => {
    const peopleEmailStateTemp: PeopleEmailState = {};

    people.forEach((person) => {
      peopleEmailStateTemp[person.face.elementId] = {
        email: person.face.email || '',
        status: FACE_EMAIL_STATUS.NOT_SENT,
        err: null,
      };
    });

    setEmailState(peopleEmailStateTemp);
  }, [people]);

  const { completePeople, incompletePeople, maClipsState } = useMemo(() => {
    const completePeople: Person[] = [];
    const incompletePeople: Person[] = [];
    const maClipsState: Record<string, MAClips> = {};

    people.forEach((person: Person) => {
      const id = person.face.elementId;

      const { selectedClips, missingClips, isEnoughClips } =
        getPersonWildcardsData(id, requiredShots, clipSelectionsDefault);

      const personUpd = { ...person, selectedClips };

      if (isEnoughClips) completePeople.push(personUpd);
      else incompletePeople.push(personUpd);

      maClipsState[id] = {
        selectedClips,
        missingClips,
        isEnoughClips,
        requiredClipsData: getClipsDataObj(missingClips, id),
        allClipsData: getClipsDataObj(requiredShots, id),
      };
    });

    return { completePeople, incompletePeople, maClipsState };
  }, [people, requiredShots, clipSelectionsDefault, getClipsDataObj]);

  const { canBulkShare, sharePopoverData } = useMemo(() => {
    const canBulkShare = incompletePeople.some(
      (p) => !!emailState[p.face.elementId]?.email,
    );

    const sharePopoverData = {
      header: 'Request from all',
      body: canBulkShare
        ? 'Send email to all with email below and request missing assets'
        : isEmpty(incompletePeople)
          ? 'Unable to request in bulk for complete people. Please send emails individually'
          : 'Unable to request, as no attendee emails are provided. Fill in the emails below, and share either individually or in bulk',
    };

    return { canBulkShare, sharePopoverData };
  }, [incompletePeople, emailState]);

  if (!currentProject) return;

  const handleBulkSend = async () => {
    setIsEmailsSending(true);

    await sendBulkFaceEmail({
      type: 'missing-clips-required',
      project: currentProject,
      people: incompletePeople,
      maClipsState,
      emailState,
      setEmailState,
    });

    setIsEmailsSending(false);
  };

  const renderPeopleContainer = (people: Person[]) => {
    if (isEmpty(people) || isEmpty(emailState) || isEmpty(maClipsState))
      return null;

    return (
      <div className="w-full flex flex-wrap gap-x-12 gap-y-8">
        {people.map((person) => (
          <MaPersonBlock
            key={person.face.elementId}
            person={person}
            requiredShots={requiredShots}
            maClips={maClipsState[person.face.elementId]}
            emailState={emailState}
            setEmailState={setEmailState}
            handleViewClips={() => setOpenedPersonId(person.face.elementId)}
          />
        ))}
      </div>
    );
  };

  const isComplete = isEmpty(incompletePeople);

  return (
    <React.Fragment>
      <MaAccordion
        icon={<CardsThree size="1.5rem" />}
        heading="Clips"
        subheading={
          !isAnalysed ? (
            <>The assets haven't been analysed yet</>
          ) : !isComplete ? (
            <>
              <b>{getPluralPhrase(incompletePeople, 'person')}</b> missing some
              clips
            </>
          ) : (
            <>All people have enough clips</>
          )
        }
        isComplete={isComplete}
        isOpen={!isEmpty(people)}
        isDisabled={!isAnalysed}
      >
        {!isEmpty(people) ? (
          <React.Fragment>
            <Alert
              variant="grey"
              alertData={{
                variant: 'info',
                text: (
                  <>
                    <span className="font-bold">Note</span>: We will ask
                    attendees to upload their videos within 24 hours, so after
                    that, you will need to go back to{' '}
                    <Link to={getUrl(URL.VIDEO_BUILDER, eventId)}>
                      Video Builder
                    </Link>{' '}
                    and hit 'Analyse' again.
                  </>
                ),
              }}
            />

            <div className="w-full flex items-center gap-4">
              <Button
                onClick={handleBulkSend}
                variant="pill"
                colour="black"
                className="w-full sm:w-52"
                isSubmitBtn
                isProcessing={isEmailsSending}
                disabled={!canBulkShare}
                allowPointerEvents
                popover={sharePopoverData}
              >
                Request from All
              </Button>

              <div className="text-sm text-black/[0.5]">
                The attendees will receive{' '}
                <Button
                  onClick={() => setShowEmailPreviewModal(true)}
                  variant="link"
                >
                  Missing Clips
                </Button>{' '}
                email
              </div>
            </div>

            {renderPeopleContainer(incompletePeople)}

            {!isEmpty(completePeople) && !isEmpty(incompletePeople) && (
              <hr className="border-gray-300" />
            )}

            {!isEmpty(completePeople) && (
              <div className="w-full flex flex-col items-center gap-6 sm:gap-4">
                <PageSubHeader
                  heading="Complete People"
                  subheading="The following attendees have enough clips to generate a personalised video, but you can still request more if needed."
                />

                {renderPeopleContainer(completePeople)}
              </div>
            )}
          </React.Fragment>
        ) : (
          <Alert
            variant="green"
            alertData={{
              variant: 'info',
              text: 'No requests to send – you may not have uploaded any people yet, or all videos have already been generated',
            }}
          />
        )}
      </MaAccordion>

      <EmailPreviewModal
        show={showEmailPreviewModal}
        onHide={() => setShowEmailPreviewModal(false)}
        template={emailTemplate}
        customText={getClipsDataObj(requiredShots).text}
      />

      <PersonClipsModal
        openedPersonId={openedPersonId}
        setOpenedPersonId={setOpenedPersonId}
        people={(completePeople || []).concat(incompletePeople || [])}
        hlReel={hlReel}
        requiredShots={requiredShots}
        noteText={
          <>
            Please note that this is just a preview. Changes made here won’t be
            saved. To reselect clips and generate videos, head to the{' '}
            <Link to={getUrl(URL.VB_REVIEW, eventId)}>Review</Link> page.
          </>
        }
      />
    </React.Fragment>
  );
};

export default MaClipsBlock;
